import clsx from 'clsx';
import { isEqual, uniq } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useToggle } from 'react-use';
import { CancelButton } from 'src/components/Buttons';
import { OkButton } from 'src/components/Buttons/OkButton';
import { useAppContext } from 'src/contexts/AppContext';
import { Content } from 'src/contexts/ContentContext';
import { typography } from 'src/core/themes/shared.css';
import { ReportConfig } from 'src/hooks/useReportConfigs';
import { useTagsForEntityType } from 'src/hooks/useTagsForEntityType';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { TableColumnsInput } from 'src/modals/ColumnSettings/TableColumnsInput';
import { SALE_REPORT_TABLE_COLUMNS_CONFIG } from 'src/tables/SalesTable/configs/SaleReportTableColumnsConfig';
import { filterColumnsByFeatures } from 'src/utils/columns/columnUtils';
import { isMetricConfigurableByGroupBy } from 'src/utils/columns/sales/salesColumnUtils';
import { filterCustomColumnsForSale } from 'src/utils/columns/sales/salesCustomColumnUtils';
import { CustomSalesColumn } from 'src/utils/columns/sales/salesCustomColumnUtils.types';
import { ContentId } from 'src/utils/constants/contentId';
import { SectionType } from 'src/utils/types/sectionType';
import {
  ActionOutboxEntityType,
  ReportGroupBy,
  UserSetting,
} from 'src/WebApiController';

import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '../../../modals/Modal';
import * as styles from './MetricsSelectionInputModal.css';

export const SalesMetricsSelectionInputModal = ({
  onClose,
  headerContentId,
  groupBy,
}: {
  onClose: () => void;
  headerContentId?: ContentId;
  groupBy?: ReportGroupBy;
}) => {
  const { appContext, loginContext } = useAppContext();
  const { setValue, watch, clearErrors } = useFormContext<ReportConfig>();
  const [isOpen, toggleIsOpen] = useToggle(true);

  const {
    value: customSaleColumns = [],
    setUserSetting: setCustomSaleColumns,
  } = useServerUserSetting<CustomSalesColumn[]>({
    id: UserSetting.SaleCustomColumns,
  });

  const { tagsMetadataNumeric } = useTagsForEntityType(
    ActionOutboxEntityType.Sale,
    true
  );

  const { tagsMetadata: tagsMetadataEvent } = useTagsForEntityType(
    ActionOutboxEntityType.SellerEvent,
    true
  );

  const customSaleColumnsFiltered = filterCustomColumnsForSale(
    customSaleColumns,
    tagsMetadataNumeric
  );

  const reportingTags = useMemo(() => {
    if (groupBy === ReportGroupBy.Event) {
      return [...(tagsMetadataNumeric ?? []), ...(tagsMetadataEvent ?? [])];
    }

    return tagsMetadataNumeric;
  }, [groupBy, tagsMetadataEvent, tagsMetadataNumeric]);

  const metrics = watch('metrics');

  const presetColumnIds = Object.values(SALE_REPORT_TABLE_COLUMNS_CONFIG)
    .map((i) => i.id)
    .filter((id) => {
      if (!id) return false;

      return isMetricConfigurableByGroupBy(id, groupBy);
    }) as string[];

  const activeTagNames = useMemo(
    () => reportingTags?.map((t) => t.key) ?? [],
    [reportingTags]
  );
  const customColumnIds = customSaleColumnsFiltered.map((c) => c.id);

  const filterColumns = useCallback(
    (columnList: string[]) => {
      return columnList.filter(
        (m) =>
          presetColumnIds.includes(m) ||
          activeTagNames.includes(m) ||
          customColumnIds.includes(m)
      );
    },
    [activeTagNames, customColumnIds, presetColumnIds]
  );

  const metricsFiltered = useMemo(
    () => filterColumns(metrics),
    [filterColumns, metrics]
  );

  const [userColumnSelection, setUserColumnSelection] = useState<string[]>(
    metricsFiltered ?? []
  );

  const storedColumnOrderSettingFiltered = useMemo(() => {
    const allColumnIdsAvailable = filterColumns(
      uniq([
        ...metricsFiltered,
        ...presetColumnIds.toSorted((a, b) => a.localeCompare(b)),
        ...customColumnIds.toSorted((a, b) => a.localeCompare(b)),
        ...activeTagNames.toSorted((a, b) => a.localeCompare(b)),
      ])
    );

    const existingColumnsOrderSetting = filterColumns(
      metricsFiltered as string[]
    );

    allColumnIdsAvailable.forEach((columnId) => {
      if (!existingColumnsOrderSetting.includes(columnId)) {
        existingColumnsOrderSetting.push(columnId);
      }
    });

    return existingColumnsOrderSetting;
  }, [
    activeTagNames,
    customColumnIds,
    filterColumns,
    metricsFiltered,
    presetColumnIds,
  ]);

  const [userColumnSorting, setUserColumnSorting] = useState<string[]>(
    filterColumnsByFeatures(
      storedColumnOrderSettingFiltered,
      SectionType.SalesReport,
      customSaleColumns,
      loginContext?.user,
      appContext?.features,
      reportingTags,
      groupBy
    )
  );

  const [userColumnIsDirty, setUserColumnIsDirty] = useState(false);

  const onSaveHandler = () => {
    const sortedColumnSelection = userColumnSorting.filter((c) =>
      userColumnSelection.includes(c)
    );
    setValue('metrics', sortedColumnSelection);
    clearErrors('metrics');
    toggleIsOpen();
  };

  useEffect(() => {
    if (!isOpen) setTimeout(onClose, 300);
  }, [isOpen, onClose]);

  // Sync the user setting to the default if the user has never set it
  useEffect(() => {
    const newColumns = filterColumnsByFeatures(
      storedColumnOrderSettingFiltered,
      SectionType.SalesReport,
      customSaleColumns,
      loginContext?.user,
      appContext?.features,
      reportingTags,
      groupBy
    );

    if (!userColumnIsDirty) {
      if (!isEqual(newColumns, userColumnSorting)) {
        setUserColumnSorting(newColumns);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customSaleColumns, activeTagNames, storedColumnOrderSettingFiltered]);

  // Keep the columns up to date in case the user changes the settings (e.g. deletes custom column)
  useEffect(() => {
    const allColumnIdsAvailable = filterColumns(
      uniq([
        ...metricsFiltered,
        ...presetColumnIds.toSorted((a, b) => a.localeCompare(b)),
        ...customColumnIds.toSorted((a, b) => a.localeCompare(b)),
        ...activeTagNames.toSorted((a, b) => a.localeCompare(b)),
      ])
    );

    let userColumnSortingFiltered = filterColumns(userColumnSorting);

    allColumnIdsAvailable.forEach((columnId) => {
      if (!userColumnSortingFiltered.includes(columnId)) {
        userColumnSortingFiltered.push(columnId);
      }
    });

    userColumnSortingFiltered = filterColumnsByFeatures(
      storedColumnOrderSettingFiltered,
      SectionType.SalesReport,
      customSaleColumns,
      loginContext?.user,
      appContext?.features,
      reportingTags,
      groupBy
    );

    if (
      !isEqual(new Set(userColumnSortingFiltered), new Set(userColumnSorting))
    ) {
      setUserColumnSorting(userColumnSortingFiltered);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customColumnIds]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={toggleIsOpen}
        clickOutsideToClose
        unmountOnClose
        centered
        size="sm"
      >
        <ModalHeader onClose={toggleIsOpen}>
          <div className={styles.headerBar}>
            <h5 className={clsx(typography.title5, styles.headerText)}>
              <Content id={headerContentId ?? ContentId.ColumnSettings} />
            </h5>
          </div>
        </ModalHeader>
        <ModalBody>
          <TableColumnsInput<CustomSalesColumn>
            userColumnSelection={userColumnSelection}
            onUserColumnSelectionChange={(newSelection) => {
              setUserColumnSelection(newSelection);
              setUserColumnIsDirty(true);
            }}
            userColumnSorting={userColumnSorting}
            onUserColumnSortingChange={(newSorting) => {
              setUserColumnSorting(newSorting);
              setUserColumnIsDirty(true);
            }}
            onCustomColumnsChange={setCustomSaleColumns}
            customColumns={customSaleColumnsFiltered}
            reportingTags={reportingTags}
            sectionType={SectionType.SalesReport}
            groupBy={groupBy}
          />
        </ModalBody>
        <ModalFooter>
          <CancelButton
            textContentId={ContentId.Cancel}
            onClick={toggleIsOpen}
          />
          <OkButton onClick={onSaveHandler} textContentId={ContentId.Save} />
        </ModalFooter>
      </Modal>
    </>
  );
};
